/**
 * =============================================================================
 * useTenantId.ts - Hook per ottenere il tenant_id dell'utente corrente
 * =============================================================================
 * 
 * Recupera il tenant_id dal profilo dell'utente autenticato.
 * Utilizza React Query per caching e deduplicazione automatica.
 * 
 * @version 1.0.0 - Multi-tenant Phase 1
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Hook che restituisce il tenant_id dell'utente corrente.
 * Il valore è cachato per tutta la sessione (staleTime: Infinity).
 */
export function useTenantId(): string | null {
  const { user } = useAuth();

  const { data: tenantId } = useQuery({
    queryKey: ["tenant_id", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("tenant_id")
        .eq("id", user.id)
        .single();
      if (error) {
        console.error("[useTenantId] Error fetching tenant_id:", error);
        return null;
      }
      return data?.tenant_id ?? null;
    },
    enabled: !!user,
    staleTime: Infinity, // Il tenant_id non cambia durante la sessione
  });

  return tenantId ?? null;
}

/**
 * Hook che restituisce il tenant_id e lancia un errore se non disponibile.
 * Da usare solo in contesti dove il tenant_id è GARANTITO (utente autenticato).
 */
export function useRequiredTenantId(): string {
  const tenantId = useTenantId();
  if (!tenantId) {
    throw new Error("tenant_id non disponibile - l'utente deve essere autenticato");
  }
  return tenantId;
}
